import Vue from 'vue';
import axios from 'axios';
import _find from 'lodash/find';
import _reject from 'lodash/reject';
import { auth } from '../utils/requests';
import { request_mutations, request_state } from '../utils/store';
import { CARD_LIST, CARD_DETAIL } from '../api';

export default {
    namespaced: true,
    state: {
        list: {}, // {<column_id>: request_state}
        detail: request_state(),
        create: request_state(),
        update: request_state(),
        remove: request_state(),
    },
    mutations: {
        ...request_mutations('detail', 'detail'),
        ...request_mutations('create', 'create'),
        ...request_mutations('update', 'update'),
        ...request_mutations('remove', 'remove'),
        list_loading(state, column) {
            if (!state.list[column]) {
                Vue.set(state.list, column, request_state());
            }
            Vue.set(state.list[column], 'loading', true);
            Vue.set(state.list[column], 'succeed', null);
            Vue.set(state.list[column], 'error', {});
        },
        list_success(state, { column, payload }) {
            Vue.set(state.list[column], 'loading', false);
            Vue.set(state.list[column], 'succeed', true);
            Vue.set(state.list[column], 'error', {});
            Vue.set(state.list[column], 'data', payload);
        },
        list_error(state, { column, payload }) {
            Vue.set(state.list[column], 'loading', false);
            Vue.set(state.list[column], 'succeed', false);
            Vue.set(state.list[column], 'error', payload);
        },
        extend_list(state, { column, payload }) {
            const results = [...state.list[column].data.results, ...payload.results];
            Vue.set(state.list[column], 'loading', false);
            Vue.set(state.list[column], 'error', {});
            Vue.set(state.list[column].data, 'results', results);
            Vue.set(state.list[column].data, 'next', payload.next);
        },
        clean_list(state) {
            state.list = [];
        },
        add_assignee(state, { column_id, card_id, assignee }) {
            const card = _find(state.list[column_id].data.results, { id: card_id });
            card.assignees.push(assignee);
        },
        remove_assignee(state, { column_id, card_id, assignee_id }) {
            const card = _find(state.list[column_id].data.results, { id: card_id });
            card.assignees = _reject(card.assignees, { id: assignee_id });
        },
        add_assignee_to_details(state, { assignee }) {
            state.detail.data.assignees.push(assignee);
        },
        remove_assignee_from_details(state, { assignee_id }) {
            state.detail.data.assignees = _reject(state.detail.data.assignees, { id: assignee_id });
        },
        add_card(state, { column, position, card }) {
            state.list[column].data.results.splice(position, 0, card);
        },
        remove_card(state, { column, position }) {
            state.list[column].data.results.splice(position, 1);
        },
        update_due(state, { column_id, card_id, due_date }) {
            const card = _find(state.list[column_id].data.results, { id: card_id });
            card.due_date = due_date;
        },
        update_milestone(state, { column_id, card_id, milestone }) {
            const card = _find(state.list[column_id].data.results, { id: card_id });
            card.milestone = milestone;
        },
        remove_tag(state, { column_id, card_id, tag_id }) {
            const card = _find(state.list[column_id].data.results, { id: card_id });
            card.note.note_tags = _reject(card.note.note_tags, { id: tag_id });
        },
        remove_tag_from_detail(state, { tag_id }) {
            state.detail.data.note.note_tags = _reject(
                state.detail.data.note.note_tags,
                { id: tag_id },
            );
        },
        add_tag(state, { column_id, card_id, note_tag }) {
            const card = _find(state.list[column_id].data.results, { id: card_id });
            card.note.note_tags.push(note_tag);
        },
    },
    actions: {
        list({ commit }, {
            space_id, flow_id, column, search, tags, extend, page,
        }) {
            const url = CARD_LIST
                .replace('{space_id}', space_id)
                .replace('{flow_id}', flow_id);

            const params = {
                column, page, tags, search,
            };

            commit('list_loading', column);
            return new Promise((resolve) => {
                axios.get(url, { params, ...auth() }).then((response) => {
                    const mutation = extend ? 'extend_list' : 'list_success';
                    commit(mutation, { column, payload: response.data });
                    resolve();
                }).catch((error) => {
                    commit('list_error', {
                        column,
                        payload: error.response ? error.response.data : {},
                    });
                });
            });
        },
        create({ commit }, { space_id, flow_id, data }) {
            const url = CARD_LIST
                .replace('{space_id}', space_id)
                .replace('{flow_id}', flow_id);

            commit('create_loading');
            return new Promise((resolve, reject) => {
                axios.post(url, data, auth()).then((response) => {
                    commit('create_success', response.data);
                    resolve();
                }).catch((error) => {
                    const errors = error.response ? error.response.data : {};
                    reject(errors);
                    commit('create_error', errors);
                });
            });
        },
        update({ commit }, {
            space_id, flow_id, card_id, data,
        }) {
            const url = CARD_DETAIL
                .replace('{space_id}', space_id)
                .replace('{flow_id}', flow_id)
                .replace('{card_id}', card_id);

            commit('update_loading');
            axios.patch(url, data, auth()).then((response) => {
                commit('update_success', response.data);
            }).catch((error) => {
                commit('update_error', error.response ? error.response.data : {});
            });
        },
        remove({ commit }, { space_id, flow_id, card_id }) {
            const url = CARD_DETAIL
                .replace('{space_id}', space_id)
                .replace('{flow_id}', flow_id)
                .replace('{card_id}', card_id);

            commit('remove_loading');
            return new Promise((resolve) => {
                axios.delete(url, auth()).then((response) => {
                    commit('remove_success', response.data);
                    resolve();
                }).catch((error) => {
                    commit('remove_error', error.response ? error.response.data : {});
                });
            });
        },
        detail({ commit }, { space_id, flow_id, card_id }) {
            const url = CARD_DETAIL
                .replace('{space_id}', space_id)
                .replace('{flow_id}', flow_id)
                .replace('{card_id}', card_id);

            commit('detail_loading');
            return new Promise((resolve) => {
                axios.get(url, auth()).then((response) => {
                    commit('detail_success', response.data);
                    resolve();
                }).catch((error) => {
                    commit('detail_error', error.response ? error.response.data : {});
                });
            });
        },
    },
};
